import axiosInstance from '../services/AxiosInstance';

const API_URL = '/batches';

// Get batches with pagination, search, and filters
export const getBatches = async (
  limit = 10,
  page = 1,
  search = '',
  searchField = 'name',
  trainerId = null,
  courseId = null,
  status = null,
  classroomId = null
) => {
  try {
    const params = {
      limit: Number(limit),
      page: Number(page),
    };

    if (search && search.trim() !== '') {
      params.search = search.trim();
      params.searchField = searchField;
    }

    if (trainerId) {
      params.trainerId = trainerId;
    }

    if (courseId) {
      params.courseId = courseId;
    }

    if (status === 'active' || status === 'ended') {
      params.status = status;
    }

    // Filtered by the API, against the classroom id the batch stores.
    if (classroomId) {
      params.classroomId = classroomId;
    }

    const response = await axiosInstance.get(API_URL, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching batches', error);
    throw error;
  }
};

/**
 * One page of batches for a picker: each row is just { id, name }.
 * Same search, trainer scoping and paging as getBatches, but the API skips
 * the full record and its course/trainer/classroom join (`fields=options`),
 * which is all a dropdown that prints a name ever needed.
 */
export const getBatchOptions = async (limit = 10, page = 1, search = '', trainerId = null) => {
  const params = { limit: Number(limit), page: Number(page), fields: 'options' };
  if (search && search.trim() !== '') {
    params.search = search.trim();
    params.searchField = 'name';
  }
  if (trainerId) params.trainerId = trainerId;
  const response = await axiosInstance.get(API_URL, { params });
  return response.data;
};

/**
 * Load all batches by paging through results. The backend clamps any single
 * page to a max of 100 records, so a single large-limit request silently
 * truncates when the true count exceeds 100 — this pages through until a
 * short page (or no more pages) is returned.
 */
export const fetchAllBatches = async (search = '', searchField = 'name') => {
  const all = [];
  let page = 1;
  const limit = 100;
  const maxPages = 200;
  while (page <= maxPages) {
    const res = await getBatches(limit, page, search, searchField);
    const chunk = Array.isArray(res?.data) ? res.data : [];
    if (!chunk.length) break;
    all.push(...chunk);
    const hasNext = Boolean(res?.pagination?.hasNext);
    // An absent totalPages means the API was not asked for a total, not
    // that there is a single page -- see fetchAllTrainers.
    const totalPages = Number.parseInt(res?.pagination?.totalPages, 10);
    const atLastPage = Number.isFinite(totalPages) && page >= totalPages;
    if (!hasNext || atLastPage || chunk.length < limit) break;
    page += 1;
  }
  return all;
};

/** Every batch as { id, name } -- fetchAllBatches for a picker's "Select all". */
export const fetchAllBatchOptions = async (search = '') => {
  const all = [];
  let page = 1;
  const limit = 100;
  const maxPages = 200;
  while (page <= maxPages) {
    const res = await getBatchOptions(limit, page, search);
    const chunk = Array.isArray(res?.data) ? res.data : [];
    if (!chunk.length) break;
    all.push(...chunk);
    const hasNext = Boolean(res?.pagination?.hasNext);
    const totalPages = Number.parseInt(res?.pagination?.totalPages, 10);
    const atLastPage = Number.isFinite(totalPages) && page >= totalPages;
    if (!hasNext || atLastPage || chunk.length < limit) break;
    page += 1;
  }
  return all;
};

// Get batch by ID
export const getBatchById = async (batchId, options = {}) => {
  try {
    const params = {};
    if (options.includeStudents) {
      params.includeStudents = 'true';
    }

    const response = await axiosInstance.get(`${API_URL}/${batchId}`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching batch details', error);
    throw error;
  }
};

// Create a new batch
export const createBatch = async (batchData) => {
  try {
    const response = await axiosInstance.post(API_URL, batchData);
    return response.data;
  } catch (error) {
    console.error('Error creating batch', error);
    throw error;
  }
};

// Update a batch
export const updateBatch = async (batchId, batchData) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${batchId}`, batchData);
    return response.data;
  } catch (error) {
    console.error('Error updating batch', error);
    throw error;
  }
};

// Delete a batch
export const deleteBatch = async (batchId, hardDelete = false) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/${batchId}?hardDelete=${hardDelete}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting batch', error);
    throw error;
  }
};

export const getBatchesWithFilters = async (limit = 20, page = 1, search = "", courseId = "", trainerId = "") => {
  try {
    let url = `/batches?limit=${limit}&page=${page}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (courseId) {
      url += `&courseId=${courseId}`;
    }
    if (trainerId) {
      url += `&trainerId=${trainerId}`;
    }
    const response = await axiosInstance.get(url);
    return {
      data: response.data?.data || [],
      pagination: response.data?.pagination || {}
    };
  } catch (error) {
    console.error("Error fetching batches with filters:", error);
    throw error;
  }
};

// Get batches for multiple trainers (POST request)
export const getBatchesByTrainers = async (
  trainerIds,
  limit = 10,
  page = 1,
  search = '',
  searchField = 'name'
) => {
  try {
    const params = {
      pageSize: Number(limit),
      currentPage: Number(page),
    };
    if (search && search.trim() !== '') {
      params.search = search.trim();
      params.searchField = searchField;
    }

    const response = await axiosInstance.post(
      '/batches/trainer-batches',
      { trainerIds },
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching batches by trainers', error);
    throw error;
  }
};



// Chatgroup services


// Create Chat Group for Batch
export const createChatGroup = async (batchId) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/${batchId}/create-chat-group`);
    return response.data;
  } catch (error) {
    console.error('Error creating chat group', error);
    throw error;
  }
};

// Delete Chat Group for Batch
export const deleteChatGroup = async (batchId, hard = false) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/${batchId}/chat-group?hard=${hard}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting chat group', error);
    throw error;
  }
};

// Sync pending chat members (retry adding students who had no auth profile)
export const syncChatMembers = async (batchId) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/${batchId}/sync-chat-members`);
    return response.data;
  } catch (error) {
    console.error('Error syncing chat members', error);
    throw error;
  }
};

// Sync pending chat members by chat group ID (used from Chat UI)
export const syncChatMembersByChatId = async (chatGroupId) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/sync-chat-members-by-chat/${chatGroupId}`);
    return response.data;
  } catch (error) {
    console.error('Error syncing chat members by chat ID', error);
    throw error;
  }
};

// Get pending chat members for a chat group
export const getPendingChatMembers = async (chatGroupId) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/pending-chat-members/${chatGroupId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching pending chat members', error);
    throw error;
  }
};

// Sync a single pending student to chat group
export const syncSingleChatMember = async (chatGroupId, studentId) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/sync-single-chat-member/${chatGroupId}`, { studentId });
    return response.data;
  } catch (error) {
    console.error('Error syncing single chat member', error);
    throw error;
  }
};


/**
 * A batch's notes, newest first: [{ date, userId, userName, note }].
 * Staff only -- the API answers 403 to anyone else.
 */
export const getBatchNotes = async (batchId) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/${batchId}/notes`);
    return Array.isArray(response.data?.data) ? response.data.data : [];
  } catch (error) {
    console.error('Error fetching batch notes', error);
    throw error;
  }
};

/**
 * The most recent `limit` notes (newest first, cut by the API) and the
 * batch's whole count: { items, total }. For the Details tab's preview.
 */
export const getRecentBatchNotes = async (batchId, limit = 5) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/${batchId}/notes`, { params: { limit } });
    return {
      items: Array.isArray(response.data?.data) ? response.data.data : [],
      total: Number(response.data?.meta?.total) || 0,
    };
  } catch (error) {
    console.error('Error fetching recent batch notes', error);
    throw error;
  }
};

export const partialUpdateBatch = async (batchId, batchData) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${batchId}`, batchData);
    return response.data;
  } catch (error) {
    console.error('Error partially updating batch', error);
    throw error;
  }
};